// feeCalculations.js — single source of truth for all fee/balance math.
// Mirrors the logic we designed in the mockups: participation fee is flat per
// dancer; entry fee is summed per dancer based on which entries they're in and
// each entry's type rate; "balance owing" subtracts whatever the org has
// already confirmed as paid.
//
// Participation Fee paid-status comes from dancer.orgParticipationFeePaid,
// which the backend keeps in sync with the real dollar amount on every write
// (see PATCH /api/admin/participants/:id/fee). Entry Fee paid-status has no
// such boolean — it's tracked entirely in the Fee Payments ledger (see
// shared/utils/feePaymentLedger.js's paidForParticipant) — so every function
// below that touches entry fees takes entryFeePayments (that school's ledger
// rows, from useTeacherData()) instead of trusting a stored flag.

import { paidForParticipant } from '../../shared/utils/feePaymentLedger';

export function entriesForDancer(dancerId, entries) {
  return (entries || []).filter((e) => (e.dancerIds || []).includes(dancerId));
}

// Resolves an entry to its price-sheet row. Freestyle and Creative
// Collaboration are priced by entry type rather than size (Freestyle entries
// always save size = 'Solo', which would otherwise overcharge them at the
// Solo rate). Grad Solo bills at the same rate as Solo — there's no separate
// "Grad Solo" row in the Prices sheet.
export function entryRateFor(entry, entryRates) {
  if (entry.level === 'Freestyle') return entryRates['Freestyle'] || 0;
  if (entry.style === 'Creative Collaboration') return entryRates['Creative Collaboration'] || 0;
  const sizeKey = entry.size === 'Grad Solo' ? 'Solo' : entry.size;
  return entryRates[sizeKey] || 0;
}

export function entryFeeTotalForDancer(dancerId, entries, entryRates) {
  return entriesForDancer(dancerId, entries).reduce(
    (sum, e) => sum + entryRateFor(e, entryRates),
    0
  );
}

export function balanceOwingForDancer(dancer, entries, feeConfig, entryFeePayments) {
  const { ENTRY_RATES, PARTICIPATION_FEE } = feeConfig;
  const entryFeeTotal = entryFeeTotalForDancer(dancer.id, entries, ENTRY_RATES);
  const entryFeePaid = paidForParticipant(dancer.id, entryFeePayments);
  let balance = 0;
  if (!dancer.orgParticipationFeePaid) balance += PARTICIPATION_FEE;
  balance += Math.max(0, entryFeeTotal - entryFeePaid);
  return balance;
}

export function paymentStatusForDancer(dancer, entries, feeConfig, entryFeePayments) {
  const entryFeeTotal = entryFeeTotalForDancer(dancer.id, entries, feeConfig.ENTRY_RATES);
  const entryFeePaid = paidForParticipant(dancer.id, entryFeePayments);
  const entryFeeSettled = entryFeeTotal === 0 || entryFeePaid >= entryFeeTotal;
  if (dancer.orgParticipationFeePaid && entryFeeSettled) return 'fully-paid';
  if (dancer.orgParticipationFeePaid) return 'participation-paid';
  if (entryFeeSettled && entryFeeTotal > 0) return 'partially-paid';
  return 'not-paid';
}

export function formatCurrency(amount) {
  return `$${amount.toFixed(2)}`;
}

// The school registration fee covers a t-shirt and banquet/gala meal for
// two teachers/chaperones — every additional person beyond that costs a flat
// fee to cover the same. See the note on the Teachers & chaperones page.
// That fee is a Prices-tab item ("Additional Staff Fee"), same as every
// other price in this app — ADDITIONAL_STAFF_FEE below is only the fallback
// used until that row exists on a given sheet, so an unconfigured sheet
// still charges the right amount instead of silently going to $0. Pass the
// real Prices-tab value in as feePerPerson wherever it's available (see
// schoolFeeSummary/staffMemberFeeOwed).
export const INCLUDED_STAFF_COUNT = 2;
export const ADDITIONAL_STAFF_FEE = 50;

export function additionalStaffFeeTotal(staffCount, feePerPerson = ADDITIONAL_STAFF_FEE) {
  return Math.max(0, (staffCount || 0) - INCLUDED_STAFF_COUNT) * feePerPerson;
}

// schoolRegistrationFee is the flat, once-per-school fee (not per dancer) —
// it belongs in the school's full invoice total but must never be folded
// into any individual dancer's balance owing.
export function schoolFeeSummary(dancers, entries, feeConfig, schoolRegistrationFee, staffCount = 0, entryFeePayments) {
  const { ENTRY_RATES, PARTICIPATION_FEE } = feeConfig;
  const totalDancers = dancers.length;
  const participationPaidCount = dancers.filter((d) => d.orgParticipationFeePaid).length;
  const participationRemaining = (totalDancers - participationPaidCount) * PARTICIPATION_FEE;

  let entryFeeRemaining = 0;
  let entryFeeTotal = 0;
  dancers.forEach((d) => {
    const fee = entryFeeTotalForDancer(d.id, entries, ENTRY_RATES);
    entryFeeTotal += fee;
    entryFeeRemaining += Math.max(0, fee - paidForParticipant(d.id, entryFeePayments));
  });

  const schoolRegistrationRemaining = (schoolRegistrationFee && !schoolRegistrationFee.paid)
    ? (schoolRegistrationFee.amount || 0)
    : 0;

  // ENTRY_RATES is the raw Prices-tab map (see getFeeConfig), so this picks
  // up an "Additional Staff Fee" row the same way every other price does.
  const additionalStaffFeeRemaining = additionalStaffFeeTotal(staffCount, ENTRY_RATES['Additional Staff Fee'] ?? ADDITIONAL_STAFF_FEE);

  return {
    totalDancers,
    participationPaidCount,
    participationRemaining,
    entryFeeTotal,
    entryFeeRemaining,
    schoolRegistrationRemaining,
    additionalStaffFeeRemaining,
    totalOutstanding: participationRemaining + entryFeeRemaining + schoolRegistrationRemaining + additionalStaffFeeRemaining,
  };
}
